import React, { useCallback, useEffect, useState } from "react";
import { ClipboardList, Send, UploadCloud, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import "../styles/pages/Feedback.css";
import API_BASE from "../api";
import { trackProductEvent } from "../analytics/trackEvent";
import DesktopRightRail from "../components/DesktopRightRail";

export default function Feedback({
  currentUser,
  posts = [],
  users = [],
  onOpenUser,
  enableDesktopRightRail = false,
  desktopRightRailTargetId,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [category, setCategory] = useState("");
  const [area, setArea] = useState("");
  const [categorySpecify, setCategorySpecify] = useState("");
  const [areaSpecify, setAreaSpecify] = useState("");
  const [message, setMessage] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [status, setStatus] = useState(null); // success | error | loading
  const [deviceOS, setDeviceOS] = useState("");
  const [desktopRightRailTarget, setDesktopRightRailTarget] = useState(null);

  useEffect(() => {
    if (!desktopRightRailTargetId || typeof document === "undefined") {
      setDesktopRightRailTarget(null);
      return;
    }

    setDesktopRightRailTarget(
      document.getElementById(desktopRightRailTargetId)
    );
  }, [desktopRightRailTargetId]);

  const isSubmitDisabled =
    !message.trim() ||
    !category ||
    (category === "other" && !categorySpecify.trim()) ||
    (area === "other" && !areaSpecify.trim()) ||
    status === "loading";

  const closeFeedback = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  const submitFeedback = async () => {
    const trimmedCategorySpecify = categorySpecify.trim();
    const trimmedAreaSpecify = areaSpecify.trim();

    if (
      !message.trim() ||
      !category ||
      (category === "other" && !trimmedCategorySpecify) ||
      (area === "other" && !trimmedAreaSpecify)
    ) {
      return;
    }

    setStatus("loading");

    try {
      const formData = new FormData();
      formData.append("category", category);
      formData.append("area", area);
      if (category === "other") {
        formData.append("categoryDetails", trimmedCategorySpecify);
      }
      if (area === "other") {
        formData.append("areaDetails", trimmedAreaSpecify);
      }
      formData.append("message", message);
      formData.append("deviceOS", deviceOS);
      if (screenshot) {
        formData.append("screenshot", screenshot);
      }

      const res = await fetch(`${API_BASE}/feedback`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Failed");

      trackProductEvent("feedback_submitted", {
        featureArea: "feedback",
        metadata: {
          source: "feedback_page",
          category,
          area: area || "",
          device_os: deviceOS || "",
          has_screenshot: Boolean(screenshot),
        },
      });
      // reset form
      setCategory("");
      setArea("");
      setCategorySpecify("");
      setAreaSpecify("");
      setMessage("");
      setScreenshot(null);
      setDeviceOS("");
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const desktopRightRail = enableDesktopRightRail ? (
    <DesktopRightRail
      currentUser={currentUser}
      posts={posts}
      users={users}
      onOpenUser={onOpenUser}
    />
  ) : null;

  return (
    <div
      className={`feedback-layout${
        enableDesktopRightRail ? " feedback-layout--with-right-rail" : ""
      }`}
    >
      <div className="feedback-page">
        <section className="feedback-container" aria-labelledby="feedback-title">
        <button
          type="button"
          className="feedback-close"
          onClick={closeFeedback}
          aria-label={t("common_close")}
        >
          <X size={18} strokeWidth={2.5} aria-hidden="true" />
        </button>

        <header className="feedback-header">
          <span className="feedback-header-icon" aria-hidden="true">
            <ClipboardList size={30} strokeWidth={2.1} />
          </span>
          <h2 id="feedback-title">{t("feedback_title")}</h2>
          <p>{t("feedback_subtitle")}</p>
        </header>

        <div className="feedback-form">
          <div className="feedback-field">
            <label htmlFor="feedback-category">
              {t("feedback_category_required")}
            </label>
            <select
              id="feedback-category"
              value={category}
              onChange={(e) => {
                const nextCategory = e.target.value;
                setCategory(nextCategory);
                if (nextCategory !== "other") {
                  setCategorySpecify("");
                }
              }}
            >
              <option value="" disabled>
                {t("feedback_category_placeholder")}
              </option>
              <option value="bug">{t("feedback_category_bug")}</option>
              <option value="idea">{t("feedback_category_idea")}</option>
              <option value="content">{t("feedback_category_content")}</option>
              <option value="incorrect_information">
                {t("feedback_category_incorrect_information")}
              </option>
              <option value="other">{t("feedback_category_other")}</option>
            </select>
          </div>

          {category === "other" && (
            <div className="feedback-field">
              <label htmlFor="feedback-category-specify">
                {t("feedback_specify_label")}
              </label>
              <input
                id="feedback-category-specify"
                type="text"
                value={categorySpecify}
                onChange={(e) => setCategorySpecify(e.target.value)}
                placeholder={t("feedback_specify_label")}
              />
            </div>
          )}

          <div className="feedback-field">
            <label htmlFor="feedback-area">{t("feedback_feature_label")}</label>
            <select
              id="feedback-area"
              value={area}
              onChange={(e) => {
                const nextArea = e.target.value;
                setArea(nextArea);
                if (nextArea !== "other") {
                  setAreaSpecify("");
                }
              }}
            >
              <option value="">{t("feedback_feature_placeholder")}</option>
              <option value="messages">{t("feedback_feature_messages")}</option>
              <option value="search">{t("feedback_feature_search")}</option>
              <option value="groups">{t("feedback_feature_groups")}</option>
              <option value="market">{t("feedback_feature_market")}</option>
              <option value="trips">{t("feedback_feature_trips")}</option>
              <option value="catches">{t("feedback_feature_catches")}</option>
              <option value="fishipedia">{t("feedback_feature_fishipedia")}</option>
              <option value="fish_species">
                {t("feedback_feature_fish_species")}
              </option>
              <option value="local_regulations">
                {t("feedback_feature_local_regulations")}
              </option>
              <option value="weather">{t("feedback_feature_weather")}</option>
              <option value="tides">{t("feedback_feature_tides")}</option>
              <option value="map">{t("feedback_feature_map")}</option>
              <option value="feed_posts">{t("feedback_feature_feed_posts")}</option>
              <option value="profile">{t("feedback_feature_profile")}</option>
              <option value="notifications">
                {t("feedback_feature_notifications")}
              </option>
              <option value="user_rankings">
                {t("feedback_feature_user_rankings")}
              </option>
              <option value="feedback">{t("feedback_feature_feedback")}</option>
              <option value="other">{t("feedback_feature_other")}</option>
            </select>
          </div>

          {area === "other" && (
            <div className="feedback-field">
              <label htmlFor="feedback-area-specify">
                {t("feedback_specify_label")}
              </label>
              <input
                id="feedback-area-specify"
                type="text"
                value={areaSpecify}
                onChange={(e) => setAreaSpecify(e.target.value)}
                placeholder={t("feedback_specify_label")}
              />
            </div>
          )}

          <div className="feedback-field">
            <label htmlFor="feedback-device">{t("feedback_device_label")}</label>
            <select
              id="feedback-device"
              value={deviceOS}
              onChange={(e) => setDeviceOS(e.target.value)}
            >
              <option value="">{t("feedback_device_placeholder")}</option>
              <option value="ios">{t("feedback_device_ios")}</option>
              <option value="android">{t("feedback_device_android")}</option>
              <option value="windows">{t("feedback_device_windows")}</option>
              <option value="macos">{t("feedback_device_macos")}</option>
              <option value="linux">{t("feedback_device_linux")}</option>
              <option value="tablet">{t("feedback_device_tablet")}</option>
              <option value="other">{t("feedback_device_other")}</option>
            </select>
          </div>

          <div className="feedback-field">
            <label htmlFor="feedback-message">{t("feedback_message")}</label>
            <textarea
              id="feedback-message"
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("feedback_message_placeholder")}
            />
          </div>

          <div className="feedback-field">
            <span className="feedback-upload-label">{t("feedback_screenshot")}</span>
            <input
              id="feedback-screenshot"
              className="feedback-file-input"
              type="file"
              accept="image/*"
              onChange={(e) => setScreenshot(e.target.files[0] || null)}
            />
            <label className="feedback-upload" htmlFor="feedback-screenshot">
              <span className="feedback-upload-icon" aria-hidden="true">
                <UploadCloud size={22} strokeWidth={2.2} />
              </span>
              <span className="feedback-upload-copy">
                <span>{t("feedback_screenshot_hint")}</span>
                <small>{screenshot ? screenshot.name : t("feedback_no_file")}</small>
              </span>
              <span className="feedback-upload-action">
                {t("feedback_screenshot_button")}
              </span>
            </label>
          </div>

          <button
            type="button"
            className="feedback-submit"
            onClick={submitFeedback}
            disabled={isSubmitDisabled}
          >
            {status === "loading" ? (
              t("feedback_sending")
            ) : (
              <>
                <Send size={18} strokeWidth={2.2} aria-hidden="true" />
                <span>{t("feedback_submit")}</span>
              </>
            )}
          </button>

          {status === "success" && (
            <p className="feedback-success">{t("feedback_thank_you")}</p>
          )}

          {status === "error" && (
            <p className="feedback-error">{t("feedback_error")}</p>
          )}
        </div>
        </section>
      </div>
      {desktopRightRail && desktopRightRailTarget
        ? createPortal(desktopRightRail, desktopRightRailTarget)
        : desktopRightRail && !desktopRightRailTargetId
          ? desktopRightRail
          : null}
    </div>
  );
}
